import * as Yup from "yup";
import { Program } from "../../../../../models/program";
import { RoleIndex } from "../../../../../models/role";
import { UserType } from "../../../../../models/userType";

// The client type carries the program. SM reads `client_type`, training reads
// `client_user_type`; the two mirror each other at the same indices.
export const AnalyticsClientTypes: number[] = [
  UserType.Index.SalesRep,
  UserType.Index.Manager,
];

export const TrainingClientTypes: number[] = [
  UserType.Index.LWS_ADMIN,
  UserType.Index.LWS_DEALER,
  UserType.Index.LWS_SCHOOL,
  UserType.Index.LWS_SUPPORT,
];

// Scope to every record; the backend turns this into people_user's 1=1.
export const AllRecordsTrainingTypes: number[] = [
  UserType.Index.LWS_ADMIN,
  UserType.Index.LWS_SUPPORT,
];

/** The program a client type grants, from the table in models/program.ts. */
export const programForClientType = (index?: number): number | undefined => {
  if (index === undefined) return undefined;
  const match = Object.entries(Program.AllowedUserTypesIndex).find(([, types]) =>
    (types as readonly number[]).includes(index)
  );
  return match ? Number(match[0]) : undefined;
};

export const isAnalyticsType = (index?: number) =>
  index !== undefined && AnalyticsClientTypes.includes(index);

export const isTrainingType = (index?: number) =>
  index !== undefined && TrainingClientTypes.includes(index);

// A list, though client_type_index is a single column today. When
// clients_user_types lands, only the initial-values mapping changes.
export const programsFor = (clientTypeIndex?: number): number[] =>
  clientTypeIndex === undefined ? [] : [clientTypeIndex];

// Roles that hold a list of clients rather than a single one.
export const isMultiClientRole = (roleIndex?: number) =>
  roleIndex === RoleIndex.Admin || roleIndex === RoleIndex.AccountAdmin;

// Only a ClientUser carries a client type, and therefore a program profile.
export const hasClientType = (roleIndex?: number) =>
  roleIndex === RoleIndex.ClientUser;

// AccountAdmin has a title but no branch/department/type.
export const hasTitleOnly = (roleIndex?: number) =>
  roleIndex === RoleIndex.AccountAdmin;

export interface UserFormValues {
  email: string;
  first_name: string;
  last_name: string;
  /** Full role option object: { role, role_index, role_label } */
  user_role: any;
  /** Single client, for RoleIndex.ClientUser */
  client: any;
  /** Client list, for Admin / AccountAdmin */
  clients: any;
  /** Full client-type option: { client_type_index, client_type_name, label } */
  client_type: any;
  sale_rep: any;
  client_title: any;
  client_branch: any;
  client_department: any;
  /** Training profile — a school object, a domain string, or all-records scope */
  user_school: any;
  user_domain: any;
  user_admin: any;
}

export const emptyUserForm: UserFormValues = {
  email: "",
  first_name: "",
  last_name: "",
  user_role: undefined,
  client: undefined,
  clients: undefined,
  client_type: undefined,
  sale_rep: undefined,
  client_title: undefined,
  client_branch: undefined,
  client_department: undefined,
  user_school: undefined,
  user_domain: undefined,
  user_admin: undefined,
};

const roleIndexOf = (v: any) => v?.role_index;
const clientTypeIndexOf = (v: any) => v?.client_type_index;

// A field is required exactly when its section is on screen, so "never show
// another program's fields" and "never demand them" are one rule.
export const userValidationSchema = Yup.object({
  email: Yup.string().email("Invalid email").required("Email is Required"),
  first_name: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("First Name is Required"),
  last_name: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("Last Name is Required"),

  user_role: Yup.mixed().required("User Role is required"),

  client: Yup.mixed().when("user_role", {
    is: (r: any) => roleIndexOf(r) === RoleIndex.ClientUser,
    then: (s) => s.required("Client is required"),
    otherwise: (s) => s.optional(),
  }),

  clients: Yup.mixed().when("user_role", {
    is: (r: any) => isMultiClientRole(roleIndexOf(r)),
    then: (s) => s.required("Client is required"),
    otherwise: (s) => s.optional(),
  }),

  client_type: Yup.mixed().when("user_role", {
    is: (r: any) => roleIndexOf(r) === RoleIndex.ClientUser,
    then: (s) => s.required("Client Type is required"),
    otherwise: (s) => s.optional(),
  }),

  // Analytics: a ClientUser on an analytics type, or an AccountAdmin.
  client_title: Yup.mixed().when(["user_role", "client_type"], {
    is: (r: any, t: any) =>
      hasTitleOnly(roleIndexOf(r)) ||
      (roleIndexOf(r) === RoleIndex.ClientUser &&
        isAnalyticsType(clientTypeIndexOf(t))),
    then: (s) => s.required("Client Title is required"),
    otherwise: (s) => s.optional(),
  }),

  client_branch: Yup.mixed().when(["user_role", "client_type"], {
    is: (r: any, t: any) =>
      roleIndexOf(r) === RoleIndex.ClientUser &&
      isAnalyticsType(clientTypeIndexOf(t)),
    then: (s) => s.required("Client Branch is required"),
    otherwise: (s) => s.optional(),
  }),

  client_department: Yup.mixed().when(["user_role", "client_type"], {
    is: (r: any, t: any) =>
      roleIndexOf(r) === RoleIndex.ClientUser &&
      isAnalyticsType(clientTypeIndexOf(t)),
    then: (s) => s.required("Client Department is required"),
    otherwise: (s) => s.optional(),
  }),

  sale_rep: Yup.mixed().when("client_type", {
    is: (t: any) => clientTypeIndexOf(t) === UserType.Index.SalesRep,
    then: (s) => s.required("Sale Rep is required"),
    otherwise: (s) => s.optional(),
  }),

  // Training: the scope rule behind people_user's stored predicate.
  user_school: Yup.mixed().when("client_type", {
    is: (t: any) => clientTypeIndexOf(t) === UserType.Index.LWS_SCHOOL,
    then: (s) => s.required("School is required"),
    otherwise: (s) => s.optional(),
  }),

  user_domain: Yup.mixed().when("client_type", {
    is: (t: any) => clientTypeIndexOf(t) === UserType.Index.LWS_DEALER,
    then: (s) => s.required("Domain is required"),
    otherwise: (s) => s.optional(),
  }),

  user_admin: Yup.mixed().optional(),
});
